import React, { useState } from 'react';
import { toast } from 'sonner';
import { Rocket, ExternalLink, CheckCircle2, Copy, Info } from 'lucide-react';
import axios from 'axios';
import { superApi } from './SuperAdminAuth';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Super-admin tenant onboarding.
 *
 * Wraps the existing POST /shopify/direct-onboard endpoint (the same one used
 * by the public /onboard page + Shopify app-install callback). After creating
 * the company, optionally triggers a platform welcome email via
 * /super/email/test with a custom body, then links back to Companies.
 */
const SuperOnboardingPage = () => {
  const [form, setForm] = useState({
    company_name: '',
    full_name: '',
    email: '',
    password: '',
    send_welcome_email: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const generatePassword = () => {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let out = '';
    for (let i = 0; i < 16; i++) out += charset[Math.floor(Math.random() * charset.length)];
    setForm(f => ({ ...f, password: out }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.email || !form.full_name || !form.password) {
      toast.error('All fields are required');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      // Use public direct-onboard endpoint (the same one /onboard uses)
      const r = await axios.post(`${API}/shopify/direct-onboard`, {
        company_name: form.company_name,
        email: form.email,
        full_name: form.full_name,
        password: form.password,
      });
      const created = r.data;

      // Optional welcome email via platform email
      let emailSent = false;
      if (form.send_welcome_email) {
        try {
          const subject = `Welcome to Opswarex — your account is ready`;
          const origin = window.location.origin;
          const html = `
            <div style="font-family: -apple-system, BlinkMacSystemFont, sans-serif; max-width: 560px; margin: 0 auto; padding: 24px;">
              <h2 style="color: #111;">Welcome, ${form.full_name} 👋</h2>
              <p>Your Opswarex workspace <strong>${form.company_name}</strong> is ready.</p>
              <p><strong>Login:</strong> <a href="${origin}/login">${origin}/login</a><br/>
                 <strong>Email:</strong> ${form.email}<br/>
                 <strong>Temporary password:</strong> <code style="background: #f3f3f3; padding: 2px 6px; border-radius: 4px;">${form.password}</code></p>
              <p style="color: #666; font-size: 12px;">Please change your password on first login. If you need help, reply to this email.</p>
            </div>`;
          await superApi.post('/super/email/test', {
            to_email: form.email,
            subject,
            body_html: html,
          });
          emailSent = true;
        } catch (err) {
          toast.warning('Company created, but welcome email failed. You can resend from Platform Email page.');
        }
      }

      setResult({ ...created, welcome_email_sent: emailSent });
      toast.success('Tenant created');
      setForm({ company_name: '', full_name: '', email: '', password: '', send_welcome_email: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onboarding failed');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  return (
    <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="super-onboarding">
      <div className="flex items-center gap-2 mb-6">
        <Rocket className="w-6 h-6 text-amber-400" />
        <h1 className="text-2xl font-semibold text-white">New Tenant</h1>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 mb-6 flex items-start gap-2 text-xs text-slate-400">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
        <div>
          Creates a new company + admin user using the same flow as the public
          <a href="/onboard" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline mx-1">/onboard</a>
          page. Admins installing via the Shopify App Store are onboarded automatically — use this page for
          direct sales / manual onboarding.
        </div>
      </div>

      {result ? (
        <div className="bg-slate-900 border border-emerald-500/30 rounded-xl p-6" data-testid="onboarding-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <h2 className="text-lg font-semibold text-white">Tenant created</h2>
          </div>
          <dl className="space-y-3 text-sm">
            <Field label="Company" value={result.company?.name} />
            <Field label="Company ID" value={result.company?.id} onCopy={() => copy(result.company?.id)} />
            <Field label="Admin email" value={result.user?.email} onCopy={() => copy(result.user?.email)} />
            <Field label="Welcome email" value={result.welcome_email_sent ? 'Sent' : 'Not sent'} />
          </dl>
          <div className="mt-6 flex gap-2">
            <a
              href={`/superadmin/companies`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium"
            >
              View in Companies <ExternalLink className="w-3 h-3" />
            </a>
            <button
              onClick={() => setResult(null)}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
            >
              Create another
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-4">
          <FormRow label="Company name">
            <input type="text" required value={form.company_name} onChange={update('company_name')}
                   className={inputCls} placeholder="Acme Inc" data-testid="onboarding-company-name" />
          </FormRow>
          <FormRow label="Admin full name">
            <input type="text" required value={form.full_name} onChange={update('full_name')}
                   className={inputCls} placeholder="Jane Doe" data-testid="onboarding-full-name" />
          </FormRow>
          <FormRow label="Admin email">
            <input type="email" required value={form.email} onChange={update('email')}
                   className={inputCls} placeholder="jane@acme.com" data-testid="onboarding-email" />
          </FormRow>
          <FormRow label="Temporary password">
            <div className="flex gap-2">
              <input type="text" required value={form.password} onChange={update('password')}
                     minLength={8} className={inputCls} placeholder="Min 8 characters"
                     data-testid="onboarding-password" />
              <button type="button" onClick={generatePassword}
                      className="shrink-0 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-xs text-slate-200">
                Generate
              </button>
            </div>
          </FormRow>
          <label className="flex items-center gap-2 text-sm text-slate-300 mt-2 cursor-pointer">
            <input type="checkbox" checked={form.send_welcome_email} onChange={update('send_welcome_email')}
                   className="accent-amber-500" data-testid="onboarding-send-welcome" />
            Send welcome email with credentials (uses platform email)
          </label>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-medium disabled:opacity-50"
            data-testid="onboarding-submit"
          >
            {submitting ? 'Creating…' : 'Create tenant'}
          </button>
        </form>
      )}
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-950 border border-slate-800 text-white text-sm focus:outline-none focus:border-amber-500/50';

const FormRow = ({ label, children }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</label>
    {children}
  </div>
);

const Field = ({ label, value, onCopy }) => (
  <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-800 last:border-0">
    <dt className="text-xs uppercase tracking-wider text-slate-500">{label}</dt>
    <dd className="text-sm text-slate-200 font-mono flex items-center gap-2 truncate">
      <span className="truncate max-w-xs">{value || '—'}</span>
      {onCopy && value && (
        <button onClick={onCopy} className="text-slate-500 hover:text-amber-400"><Copy className="w-3 h-3" /></button>
      )}
    </dd>
  </div>
);

export default SuperOnboardingPage;
